import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { BookOpen, Plus, ExternalLink, Pencil, Trash2, Search, Tag, X, Folder } from 'lucide-react';

const API_URL = window.location.origin + '/api';

const PRESET_CATEGORIES = ['General', 'Hardware', 'Software', 'Red', 'Acceso', 'Impresión', 'Seguridad', 'Microsoft 365', 'Google Workspace'];

const EMPTY_FORM = { title: '', description: '', url: '', category: 'General', tags: '' };

// Renderizador markdown simple (sin dependencias externas)
function renderMarkdown(text) {
  if (!text) return '';
  let html = text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    // Encabezados
    .replace(/^### (.+)$/gm, '<h4 style="margin:10px 0 4px;font-size:12px;font-weight:600;color:var(--ink-2);text-transform:uppercase;letter-spacing:.5px">$1</h4>')
    .replace(/^## (.+)$/gm,  '<h3 style="margin:12px 0 5px;font-size:13px;font-weight:700;color:var(--ink);border-bottom:1px solid var(--border);padding-bottom:3px">$1</h3>')
    .replace(/^# (.+)$/gm,   '<h2 style="margin:14px 0 6px;font-size:14px;font-weight:700;color:var(--ink)">$1</h2>')
    // Negrita e itálica
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g,     '<em>$1</em>')
    // Código inline
    .replace(/`(.+?)`/g, '<code style="background:var(--bg);padding:1px 5px;border-radius:3px;font-size:11px;font-family:monospace">$1</code>')
    // Listas numeradas
    .replace(/^\d+\. (.+)$/gm, '<li style="margin:3px 0;padding-left:4px">$1</li>')
    // Listas con guión/asterisco
    .replace(/^[-*] (.+)$/gm,  '<li style="margin:3px 0;padding-left:4px">$1</li>')
    // Párrafos (líneas vacías)
    .replace(/\n\n/g, '</p><p style="margin:6px 0">')
    // Saltos de línea simples
    .replace(/\n/g, '<br/>');

  // Envolver listas consecutivas en <ul>
  html = html.replace(/(<li[^>]*>.*?<\/li>)(\s*<br\/>\s*(<li[^>]*>.*?<\/li>))*/g, (match) => {
    return `<ul style="margin:6px 0;padding-left:18px;list-style:disc">${match.replace(/<br\/>/g, '')}</ul>`;
  });

  return `<p style="margin:0">${html}</p>`;
}

export default function KnowledgeBase() {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [articles, setArticles]       = useState([]);
  const [categories, setCategories]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [activeCat, setActiveCat]     = useState('all');
  const [showForm, setShowForm]       = useState(false);
  const [editing, setEditing]         = useState(null);   // article being edited
  const [form, setForm]               = useState(EMPTY_FORM);
  const [saving, setSaving]           = useState(false);
  const [deleteId, setDeleteId]       = useState(null);
  const [error, setError]             = useState('');

  const canEdit = ['admin', 'technician'].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search)              params.set('q', search);
      if (activeCat !== 'all') params.set('category', activeCat);

      const [arts, cats] = await Promise.all([
        fetch(`${API_URL}/kb?${params}`, { headers }).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/kb/categories`, { headers }).then(r => r.json()).catch(() => []),
      ]);
      setArticles(Array.isArray(arts) ? arts : []);
      setCategories(Array.isArray(cats) ? cats : []);
    } finally {
      setLoading(false);
    }
  }, [search, activeCat]); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setError(''); setShowForm(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ title: a.title, description: a.description || '', url: a.url, category: a.category, tags: (a.tags || []).join(', ') });
    setError('');
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const body = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) };
      const url  = editing ? `${API_URL}/kb/${editing.id}` : `${API_URL}/kb`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Error guardando.'); return; }
      setShowForm(false);
      load();
    } catch { setError('Error de conexión.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await fetch(`${API_URL}/kb/${deleteId}`, { method: 'DELETE', headers }).catch(() => {});
    setDeleteId(null);
    load();
  };

  // All categories from DB + presets that aren't in DB yet
  const allCats = [...new Set([...categories, ...PRESET_CATEGORIES])].sort();

  // Group articles by category for display
  const grouped = articles.reduce((acc, a) => {
    const cat = a.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(a);
    return acc;
  }, {});

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <BookOpen size={22} style={{ color: 'var(--accent)' }} />
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>Base de conocimiento</div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>Artículos y recursos de resolución</div>
          </div>
        </div>
        {canEdit && (
          <button className="btn btn-primary" onClick={openNew} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={15} /> Nuevo artículo
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 20 }}>

        {/* Sidebar categorías */}
        <div>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--bg)' }}>
              Categorías
            </div>
            {[{ id: 'all', label: 'Todos' }, ...allCats.map(c => ({ id: c, label: c }))].map(cat => (
              <button key={cat.id} onClick={() => setActiveCat(cat.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 14px', border: 'none', background: activeCat === cat.id ? 'var(--accent-dim)' : 'transparent', color: activeCat === cat.id ? 'var(--accent)' : 'var(--ink-2)', fontSize: 13, fontWeight: activeCat === cat.id ? 600 : 400, cursor: 'pointer', textAlign: 'left' }}>
                <Folder size={13} />
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Main content */}
        <div>
          {/* Buscador */}
          <div style={{ position: 'relative', marginBottom: 20 }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-3)' }} />
            <input
              className="form-input"
              style={{ paddingLeft: 34 }}
              placeholder="Buscar por título, descripción o etiqueta..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--ink-3)', fontSize: 14 }}>Cargando...</div>
          ) : articles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--ink-3)' }}>
              <BookOpen size={36} style={{ marginBottom: 12, opacity: 0.3 }} />
              <div style={{ fontSize: 14, fontWeight: 500 }}>No hay artículos</div>
              {canEdit && <div style={{ fontSize: 12, marginTop: 6 }}>Hacé clic en "Nuevo artículo" para agregar el primero</div>}
            </div>
          ) : (
            Object.entries(grouped).map(([cat, arts]) => (
              <div key={cat} style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                  {cat}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {arts.map(a => (
                    <div key={a.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <a href={a.url} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                            {a.title}
                            <ExternalLink size={12} />
                          </a>
                        </div>
                        {a.description && (
                          <div
                            style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 8, lineHeight: 1.6 }}
                            dangerouslySetInnerHTML={{ __html: renderMarkdown(a.description) }}
                          />
                        )}
                        {a.resolution_script && (
                          <details style={{ marginBottom: 8 }}>
                            <summary style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', cursor: 'pointer', userSelect: 'none', listStyle: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                              <span>▶</span> Script PowerShell de resolución
                            </summary>
                            <div style={{ marginTop: 8, position: 'relative' }}>
                              <pre style={{ background: '#1e1e2e', color: '#cdd6f4', padding: '12px 14px', borderRadius: 8, fontSize: 11, overflowX: 'auto', margin: 0, lineHeight: 1.5, fontFamily: 'monospace' }}>
                                {a.resolution_script}
                              </pre>
                              <button
                                onClick={() => navigator.clipboard.writeText(a.resolution_script)}
                                style={{ position: 'absolute', top: 8, right: 8, background: '#313244', border: 'none', color: '#cdd6f4', fontSize: 10, padding: '3px 8px', borderRadius: 4, cursor: 'pointer' }}>
                                Copiar
                              </button>
                            </div>
                          </details>
                        )}
                        {a.tags && a.tags.length > 0 && (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {a.tags.map((t, i) => (
                              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', background: 'var(--bg)', borderRadius: 20, fontSize: 11, color: 'var(--ink-2)' }}>
                                <Tag size={9} /> {t}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      {canEdit && (
                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                          <button onClick={() => openEdit(a)} title="Editar"
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--ink-3)', padding: 4, borderRadius: 4 }}>
                            <Pencil size={14} />
                          </button>
                          {user?.role === 'admin' && (
                            <button onClick={() => setDeleteId(a.id)} title="Eliminar"
                              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--red)', padding: 4, borderRadius: 4 }}>
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal crear/editar */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div className="card" style={{ width: '100%', maxWidth: 520, padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{editing ? 'Editar artículo' : 'Nuevo artículo'}</div>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}><X size={14} /></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label className="form-label">Título *</label>
                <input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">URL del artículo *</label>
                <input className="form-input" type="url" placeholder="https://support.microsoft.com/..." value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Descripción breve</label>
                <textarea className="form-input" rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Categoría</label>
                  <select className="form-input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                    {PRESET_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Tags (separados por coma)</label>
                  <input className="form-input" placeholder="windows, impresora, red" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} />
                </div>
              </div>
              {error && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 12 }}>{error}</div>}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm delete */}
      {deleteId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div className="card" style={{ maxWidth: 380, padding: 28, textAlign: 'center' }}>
            <Trash2 size={32} style={{ color: 'var(--red)', marginBottom: 12 }} />
            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8 }}>¿Eliminar artículo?</div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 20 }}>Esta acción no se puede deshacer.</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button className="btn btn-secondary" onClick={() => setDeleteId(null)}>Cancelar</button>
              <button className="btn btn-primary" style={{ background: 'var(--red)', borderColor: 'var(--red)' }} onClick={handleDelete}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
